'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { VideoProps } from '@sola/schema/lib/schema.ts';
import { Image } from '../image/image.tsx';

/**
 * Click-to-play stand-in for a provider embed.
 *
 * A YouTube embed costs ~1 MB across ~11 requests (a 467 KB player bundle among
 * them) the moment its iframe attaches, whether or not the visitor ever presses
 * play. Inline videos are usually below the fold, so `Video` renders this facade
 * first and only creates the real iframe once activated — at which point the
 * embed URL carries `autoplay=1`, so a single click still starts playback.
 *
 * Occupies the full player box so swapping in the iframe costs no layout shift.
 */
export const VideoFacade: React.FC<{
  poster?: VideoProps['poster'];
  thumbnailUrl?: string;
  onActivate: () => void;
}> = t0 => {
  const $ = _c(11);
  const {
    poster,
    thumbnailUrl,
    onActivate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["skippy"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("skippy:video.play");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const label = t2;
  let t3;
  if ($[3] !== poster || $[4] !== thumbnailUrl) {
    t3 = poster ? <Image className="Video__poster" image={poster} /> : thumbnailUrl && <span aria-hidden={true} className="Video__facade-thumbnail absolute inset-0" style={{
      backgroundImage: `url(${JSON.stringify(thumbnailUrl)})`
    }} />;
    $[3] = poster;
    $[4] = thumbnailUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="Video__facade-play absolute flex items-center justify-center"><ArkIcon iconName="play-circle" size="48" /></span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== label || $[8] !== onActivate || $[9] !== t3) {
    t5 = <button type="button" onClick={onActivate} aria-label={label} title={label} className="Video__facade absolute inset-0 flex items-center justify-center w-full h-full">{t3}{t4}</button>;
    $[7] = label;
    $[8] = onActivate;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};